import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Loader2, Mail, Info } from "lucide-react";
import AuthLayout from "@/components/layout/AuthLayout";
import api from "@/lib/api";
import { registrationApi } from "@/lib/api/registration";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/contexts/ToastContext";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const toast = useToast();
  const token = searchParams.get("token");
  const tenantSlugParam = searchParams.get("tenantSlug")?.trim() || undefined;
  const tenantSlug = tenantSlugParam ?? tenant?.slug;
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [verificationType, setVerificationType] = useState<"patron" | "tenant" | null>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const channel = new BroadcastChannel("email-verification");

    const handleVerificationSuccess = () => {
      setStatus("success");
      setMessage("Your email has been verified successfully! You can now log in to your account.");
    };

    channel.addEventListener("message", (event) => {
      if (event.data.type === "email-verified") {
        handleVerificationSuccess();
      }
    });

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "email_verified" && e.newValue === "true") {
        handleVerificationSuccess();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    if (!token) {
      setStatus("idle");
      setMessage(
        "Please check your email and click the verification link to activate your account."
      );

      if (localStorage.getItem("email_verified") === "true") {
        handleVerificationSuccess();
      }

      return () => {
        channel.close();
        window.removeEventListener("storage", handleStorageChange);
      };
    }

    setStatus("loading");

    const verifyToken = async (endpoint: string) => {
      return api.get(endpoint, {
        params: { token: token || "" },
      });
    };

    verifyToken("/registration/verify-email")
      .then((response) => {
        setStatus("success");
        setVerificationType("patron");
        setMessage(response.data.message || "Email verified successfully!");

        channel.postMessage({ type: "email-verified" });
        localStorage.setItem("email_verified", "true");
        setTimeout(() => localStorage.removeItem("email_verified"), 5000);

        setTimeout(() => {
          navigate("/login");
        }, 5000);
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.message || "";
        if (
          errorMessage.includes("not for a patron account") ||
          errorMessage.includes("not for a patron")
        ) {
          return verifyToken("/auth/verify-email");
        }
        throw error;
      })
      .then((response) => {
        if (response) {
          setStatus("success");
          setVerificationType("tenant");
          setMessage(response.data.message || "Email verified successfully!");

          channel.postMessage({ type: "email-verified" });
          localStorage.setItem("email_verified", "true");
          setTimeout(() => localStorage.removeItem("email_verified"), 5000);

          setTimeout(() => {
            navigate("/login");
          }, 5000);
        }
      })
      .catch((error) => {
        setStatus("error");
        const errorMessage = error.response?.data?.message || "";
        console.error("Verification error:", error);
        console.error("Token length:", token?.length);
        console.error("Token preview:", token?.substring(0, 20));
        setMessage(
          errorMessage ||
            "Verification failed. The link may have expired. Please request a new verification email."
        );
      });

    return () => {
      channel.close();
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [token, navigate]);

  const handleResendVerification = async () => {
    const email = resendEmail.trim();
    if (!email) {
      toast.error("Enter the email address you used to register.");
      return;
    }
    setResending(true);
    try {
      const result = await registrationApi.resendVerificationEmailByEmail(email, tenantSlug);
      toast.success(result.message);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Could not send verification email.");
    } finally {
      setResending(false);
    }
  };

  const headlineMap = {
    idle: "Check your inbox.",
    loading: "Check your inbox.",
    success: "You're all set.",
    error: "Check your inbox.",
  };

  const subtextMap = {
    idle: "We've sent a verification link to confirm your email address.",
    loading: "We're verifying your email address right now.",
    success: "Your email has been verified and your account is ready.",
    error: "Something went wrong while verifying your email.",
  };

  return (
    <AuthLayout headline={headlineMap[status]} subtext={subtextMap[status]}>
      <div className="space-y-5 text-center animate-fade-in-up">
        {/* Status icon */}
        {status === "idle" && (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 ring-8 ring-primary/5">
            <Mail className="h-9 w-9 text-primary" />
          </div>
        )}
        {status === "loading" && (
          <div className="inline-flex items-center justify-center w-20 h-20">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        )}
        {status === "success" && (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 ring-8 ring-green-50/50">
            <CheckCircle2 className="text-green-600 h-9 w-9" />
          </div>
        )}
        {status === "error" && (
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 ring-8 ring-red-50/50">
            <XCircle className="text-red-600 h-9 w-9" />
          </div>
        )}

        {/* Title + message */}
        <div>
          <h2 className="text-2xl font-bold font-heading text-slate-900">
            {status === "idle" && "Check your email"}
            {status === "loading" && "Verifying your email..."}
            {status === "success" && "Email verified"}
            {status === "error" && "Verification failed"}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{message}</p>
        </div>

        {/* Idle state — waiting for user to click email link */}
        {status === "idle" && (
          <div className="p-4 border rounded-lg border-primary/20 bg-primary/5">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-left text-slate-600">
                Click the verification link in your email to activate your account and receive your
                login details.
              </p>
            </div>
          </div>
        )}

        {/* Success state */}
        {status === "success" && (
          <div className="space-y-3">
            <div className="p-4 border border-green-200 rounded-lg bg-green-50">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <p className="text-sm text-left text-green-800">
                  Your email has been verified. You can now sign in to your account.
                </p>
              </div>
            </div>

            {verificationType === "patron" && (
              <div className="p-4 border rounded-lg border-primary/20 bg-primary/5">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm text-left text-primary">
                    If your library requires a registration fee, you'll need to complete payment
                    after signing in to access all services.
                  </p>
                </div>
              </div>
            )}

            <Button onClick={() => navigate("/login")} className="w-full" size="lg">
              Go to sign in
            </Button>
          </div>
        )}

        {/* Error state */}
        {status === "error" && (
          <div className="space-y-3">
            <div className="p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-left text-red-800">
                  The verification link may have expired or is invalid. Please request a new
                  verification email.
                </p>
              </div>
            </div>

            {token && (
              <div className="p-4 text-left border rounded-lg border-slate-200 bg-slate-50/80">
                <p className="mb-3 text-sm font-medium text-slate-900">Resend verification email</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="resend-email" className="text-slate-700">
                      Email address
                    </Label>
                    <Input
                      id="resend-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <Button
                    type="button"
                    className="w-full"
                    disabled={resending}
                    onClick={() => void handleResendVerification()}
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Resend verification email"
                    )}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate("/")} className="flex-1">
                Go home
              </Button>
              <Button onClick={() => navigate("/login")} className="flex-1">
                Go to sign in
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
